import type { ReactNode } from 'react';

export interface EmptyStateProps {
  /** Illustration ou pictogramme. Décoratif : jamais porteur d'information seul. */
  icon?: ReactNode;
  /** Ce qui se passe, en une phrase. */
  title: string;
  /** Pourquoi, ou quoi faire. Facultatif quand le titre suffit. */
  description?: ReactNode;
  /** Comment en sortir. Un état vide sans issue est une impasse. */
  action?: ReactNode;
}

/**
 * État vide (US-20).
 *
 * L'application n'en avait qu'un, réutilisé pour deux situations qui n'ont
 * rien à voir : une recherche sans résultat affichait « Aucune recette pour
 * l'instant. Ajoutez-en une à la main, ou importez-la depuis une URL ou un
 * texte collé. » — le message d'une bibliothèque vide — alors que vingt
 * recettes étaient présentes, sans mentionner le terme cherché ni proposer de
 * l'effacer (audit externe, constat 11).
 *
 * Les quatre éléments (icône, titre, description, action) ne sont pas tous
 * obligatoires, mais l'**action** l'est dès qu'il y a une impasse : c'est ce
 * qui distingue un état vide rédigé d'une zone vide.
 *
 * **La bordure pointillée a disparu** (refonte éditoriale). Un cadre en
 * pointillés est la convention d'une zone de dépôt de fichier ou d'un
 * emplacement à remplir dans un outil d'administration ; sur un écran qui dit
 * « votre journal est vide », il ajoute au vide l'idée que quelque chose
 * manque *techniquement*. Un creux (`bg-surface-sunken`) dit la même chose
 * sans l'accusation : la place existe, elle attend.
 */
export function EmptyState({ icon, title, description, action }: EmptyStateProps): React.JSX.Element {
  return (
    <div
      data-testid="empty-state"
      className="flex flex-col items-center gap-4 rounded-card bg-surface-sunken px-6 py-14 text-center"
    >
      {icon !== undefined && <span className="text-foreground-muted">{icon}</span>}
      <p className="text-heading-2">{title}</p>
      {description !== undefined && (
        <p className="max-w-[46ch] text-lede text-balance">{description}</p>
      )}
      {action !== undefined && <div className="pt-1">{action}</div>}
    </div>
  );
}
